import React, { useEffect, useRef, useState } from 'react';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import * as Actions from 'redux/actions';
import { toast } from 'components/elements/molecules/Toast/Toast';
import { AWSCognitoInstance } from 'services/AWSCognito';
import { scopes } from 'config/googleConfig';

import './GoogleSignOn.scss';

// Marks a redirect-mode sign-in, so App.js knows a code in the URL is ours.
export const GOOGLE_REDIRECT_STATE = 'averpoint-google-login';

function GoogleSignOn({
  referrer,
  signUpRole,
  actions,
  history,
  message,
  emailHandles,
  clientName,
  // Start sign-in on mount (the extension's "Login using Google"). Redirect
  // mode: the popup mode needs a click on this page first.
  autoStart,
  // Finish a redirect-mode sign-in with the code Google sent back.
  redirectCode,
  onDone,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const googleClient = useRef(null);

  useEffect(() => {
    if (redirectCode) {
      handleResponse({ code: redirectCode });
      return;
    }

    const init = () => {
      googleClient.current = window.google.accounts.oauth2.initCodeClient({
        client_id:
          '208379708790-krb2m1pog4aok6frv6rpe587ubclj4k4.apps.googleusercontent.com',
        scope: scopes.join(' '),
        ux_mode: autoStart ? 'redirect' : 'popup',
        response_type: 'code',
        callback: handleResponse,
        // Redirect mode returns here, the site root: the backend exchanges
        // the code with the same redirect URI.
        redirect_uri: window.location.origin,
        ...(autoStart ? { state: GOOGLE_REDIRECT_STATE } : {}),
      });
    };

    init();

    if (autoStart) {
      setLoading(true);
      googleClient.current.requestCode();
    }
  }, []);

  const connectToGoogle = () => {
    if (!googleClient.current) {
      return;
    }

    googleClient.current.requestCode();
  };

  const handleResponse = (response) => {
    console.log('Response received from google:', response);
    if (!response || !response.code) {
      console.error('Not a valid response received from google');
      toast(
        'Valid response not received from google. Please try again later.',
        {
          type: 'error',
        },
      );
      onDone?.();
      return;
    }

    setLoading(true);

    actions
      .loginUsingGoogle({
        googleAuthorizationCode: response.code,
        emailHandles,
      })
      .then((response) => {
        console.log('Response from averpoint on google login', response);

        if (!response.success) {
          if (response.errorCode === 'DOMAIN_NOT_ALLOWED') {
            setError(`Only emails from ${clientName} are allowed`);
            if (redirectCode) {
              // The inline error goes when this closes: show it as a toast.
              toast(`Only emails from ${clientName} are allowed`, {
                type: 'error',
              });
            }
            setLoading(false);
            onDone?.();

            return;
          }

          if (
            response.errorCode === 'SIGN_UP_REQUIRED' &&
            response.googleTokens &&
            response.email
          ) {
            // Redirect to google sign up
            const location = {
              pathname: '/auth/sso-sign-up',
              state: {
                tokenResponse: {
                  ...response.googleTokens,
                  email: response.email,
                  referrer,
                  role: signUpRole,
                },
                clientName,
              },
            };
            history.push(location);
            setLoading(false);
            onDone?.();

            return;
          }

          toast(
            'Error occurred while sign in using google. Please try again later.',
            {
              type: 'error',
            },
          );
          setLoading(false);
          onDone?.();
          return;
        }

        // Set tokens to cognito
        AWSCognitoInstance.createSession(response.username, response.tokens);

        actions
          .cognitoLogin({
            username: response.username,
            accessToken: response.tokens.IdToken,
          })
          .then((_) => {
            console.log('Successful Login. Redirecting...');
            onDone?.();
          })
          .catch((error) => {
            console.error('Error occurred while login on AverPoint', error);
            toast('Error occurred while login. Please try again later.', {
              type: 'error',
            });
            setLoading(false);
            onDone?.();
          });
      })
      .catch((error) => {
        console.error('Error from averpoint on google login', error);
        toast(
          'Error occurred while sign in using google. Please try again later.',
          {
            type: 'error',
          },
        );
        setLoading(false);
        onDone?.();
      });
  };

  return (
    <div className="google-sign-on">
      {!loading ? (
        <button
          type="button"
          className="login-with-google-btn mt-4"
          onClick={() => connectToGoogle()}
        >
          {message || 'Login using Google'}
        </button>
      ) : (
        <div className="text-center mt-4">
          <div
            className="fa fa-spin fa-spinner text-primary"
            style={{ fontSize: '40px' }}
          />
        </div>
      )}
      {error && (
        <div className="text-center">
          <h4 className="av-red">{error}</h4>
        </div>
      )}
    </div>
  );
}

function mapStateToProps(state, ownProps) {
  const searchParams = new URLSearchParams(ownProps.location.search);

  const referrer = searchParams.get('referrer');

  let signUpRole = searchParams.get('role') || ownProps.roleType;

  if (
    signUpRole !== 'community' &&
    signUpRole !== 'creator' &&
    signUpRole !== 'student' &&
    signUpRole !== 'teacher'
  ) {
    signUpRole = 'community';
  }

  return {
    referrer,
    signUpRole,
  };
}

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(Actions, dispatch),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(GoogleSignOn),
);
